<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <form @submit.prevent="validateForm">
        <div class="row">
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4" v-if="itemsCompanyCategories">
              <label class="label text-secondary">Tipo </label>
              <select
                class="form-select form-control h-45"
                aria-label="Default select example"
                v-model="company.companyCategory"
                @change="clearError('companyCategory')"
              >
                <option :value="null">-- Seleccionar --</option>
                <option
                  :value="company.id"
                  v-for="(company, i) of itemsCompanyCategories"
                  :key="i"
                >
                  {{ company.name }}
                </option>
              </select>
              <div class="input-error">{{ errors.companyCategory }}</div>
            </div>
          </div>
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Nombre</label>
              <input
                type="text"
                class="form-control h-45"
                placeholder="Ingresar el nombre de la Empresa/Institución"
                v-model.trim="company.name"
                @input="clearError('name')"
              />
              <div class="input-error">{{ errors.name }}</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">RUC</label>
              <input
                type="text"
                class="form-control h-45"
                placeholder="Ingresar el RUC de la Empresa/Institución"
                v-model.trim="company.ruc"
                maxlength="11"
                @input="clearError('ruc')"
              />
              <div v-if="errors.ruc" class="input-error">{{ errors.ruc }}</div>
            </div>
          </div>
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Dominio(s) </label>
              <input
                type="text"
                class="form-control h-45"
                placeholder="Ingresar el dominio(s)"
                v-model.trim="company.domains"
                maxlength="300"
                @input="clearError('domains')"
              />
              <small class="text-muted">
                Si ingresa varios dominios, sepárelos por comas. Ej:
                empresa.com,empresa.org
              </small>
              <div class="input-error">{{ errors.domains }}</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Descripción</label>
              <textarea
                rows="4"
                style="resize: none"
                class="form-control text-dark no-resize"
                placeholder="Ingresar la descripción de la Empresa/Institución"
                v-model.trim="company.description"
                @input="clearError('description')"
              ></textarea>
              <div v-if="errors.description" class="input-error">
                {{ errors.description }}
              </div>
            </div>
          </div>
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Alias </label>
              <textarea
                rows="4"
                style="resize: none"
                class="form-control text-dark no-resize"
                placeholder="Ingresar alias"
                v-model.trim="company.alias"
                @input="clearError('alias')"
                maxlength="1000"
              ></textarea>              
              <small class="text-muted">
                Si ingresa varias alias, sepárelos por comas. Ej:
                EmpresaUno,EmpresaDos
              </small>
              <div class="input-error">{{ errors.alias }}</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <div class="d-flex flex-wrap gap-3">
              <RouterLink to="/companies">
                <button
                  type="button"
                  class="btn btn-danger bg-danger bg-opacity-10 text-danger border-0 fw-semibold py-2 px-4"
                >
                  Cancelar
                </button>
              </RouterLink>
              <button
                class="btn btn-primary bg-primary bg-opacity-10 text-primary border-0 fw-semibold py-2 px-4"
                type="submit"
                :disabled="isLoading"
              >
                <span v-if="!isLoading">{{
                  company.id == 0 ? "Crear" : "Actualizar"
                }}</span>
                <span
                  class="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                  v-if="isLoading"
                ></span>
                <span role="status" v-if="isLoading">Procesando...</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { companySchema } from "@/schemas/companySchema";
import { defineComponent, ref, onMounted } from "vue";
import api from "@/services/api";
import { useToast } from "vue-toastification";
import { useRouter, useRoute } from "vue-router";
import { COMPANY_TYPES } from "@/utils/constants";

export default defineComponent({
  name: "CreateCompany",
  components: {},
  setup() {
    const toast = useToast();
    const route = useRoute();
    const router = useRouter();
    const isLoading = ref(false);

    const itemsCompanyCategories = ref<any[]>([]);
    const company = ref({
      id: 0,
      companyTypeId: COMPANY_TYPES.PARTICIPANTE,
      name: null,
      description: null,
      ruc: null,
      companyCategory: null,
      domains: null,
      alias: null,
      flagCompany: true,
    });
    const errors = ref<Record<string, string>>({});

    onMounted(async () => {
      await getCompanyCategories();
      var id = route.params.id;
      if (id) {
        await getCompanyById(id);
      }
    });

    const getCompanyCategories = async () => {
      try {
        const response = await api.get(`/common/company-category`);
        itemsCompanyCategories.value = response.data;
      } catch (ex) {
        console.error(ex);
      }
    };

    const validateForm = async () => {
      try {
        errors.value = {};
        await companySchema.validate(company.value, {
          abortEarly: false,
          context: {
            id: company.value.id,
            companyTypeId: company.value.companyTypeId,
            ruc: company.value.ruc,
          },
        });
        isLoading.value = true;

        await createCompany();
        isLoading.value = false;
      } catch (err: any) {
        console.log("err: " + err);
        if (err.inner) {
          err.inner.forEach((e: any) => {
            errors.value[e.path] = e.message;
          });
        }
        isLoading.value = false;
      }
    };

    const clearError = (field: string) => {
      errors.value[field] = "";
    };

    const getCompanyById = async (id: any) => {
      try {
        const response = await api.get(`/company/${id}`);
        company.value = response.data;
      } catch (ex) {
        console.error(ex);
      }
    };

    const createCompany = async () => {
      try {
        company.value.flagCompany = true;
        const response: any = await api.post(`/company`, company.value);
        if (response.data != 0) {
          toast.success(
            `La Empresa/Institución fue ${
              company.value.id == 0 ? "creada" : "actualizada"
            } con éxito.`,
            {
              timeout: 2000,
            }
          );

          router.push({ name: "CompaniesPage" });
        }
      } catch (ex) {
        toast.error("La Empresa/Institución no pudo ser creada.", {
          timeout: 2000,
        });
        console.error(ex);
      }
    };

    return {
      createCompany,
      validateForm,
      clearError,
      company,
      itemsCompanyCategories,
      errors,
      isLoading,
    };
  },
});
</script>
